import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

const SyntraMfgAbout = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/syntramfg");
  };

  const features = [
    {
      emoji: "🔗",
      title: "Real-Time Collaboration",
      desc: "Secure sharing and commenting on 3D models and data fields for seamless team coordination.",
    },
    {
      emoji: "📊",
      title: "Data-Driven Insights",
      desc: "Access real-time analytics to optimize manufacturing processes and decision-making.",
    },
    {
      emoji: "💬",
      title: "In-Context Messaging",
      desc: "Communicate directly within projects, documents, and simulations to boost productivity.",
    },
    {
      emoji: "🛠️",
      title: "Process Integration",
      desc: "Integrate engineering, design, and business processes for end-to-end efficiency.",
    },
    {
      emoji: "🎓",
      title: "Knowledge Sharing",
      desc: "Leverage expert insights to train and empower the manufacturing workforce.",
    },
    {
      emoji: "⚙️",
      title: "Simulation Tools",
      desc: "Simulate production scenarios to minimize risks and maximize outcomes.",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-950 text-gray-100 font-sans">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-xl font-bold text-cyan-300">🏭 SyntraMfg</div>
          <div className="text-[11px] text-gray-400">Synchronised Transformation in Manufacturing</div>
          <div className="text-[11px] text-yellow-200">The Future of Manufacturing, Today!</div>
        </div>

        {/* Desktop user controls */}
        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-sm text-cyan-300">👋 {userName}</div>
              <button
                onClick={() => navigate(`/syntramfgdashboard`)}
                className="p-2 rounded-md bg-gray-800 hover:bg-cyan-700 transition"
                title="Dashboard"
              >
                🏠
              </button>
              <button
                onClick={handleLogout}
                className="p-2 rounded-md bg-red-600 hover:bg-red-700 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-3 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-3 py-1 rounded-full bg-cyan-600 text-gray-900 text-sm hover:bg-cyan-500"
              >
                Register
              </button>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-2xl text-cyan-300"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* Mobile menu content */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-cyan-300 font-medium">Welcome, {userName}</div>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate(`/syntramfgdashboard`)}
                  className="p-2 rounded-md bg-gray-800 hover:bg-cyan-700"
                >
                  🏠
                </button>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-md bg-red-600 hover:bg-red-700"
                >
                  ⏻
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-center gap-6">
              <Link to="/login" className="text-cyan-300">Login</Link>
              <Link to="/signup" className="text-cyan-300">Register</Link>
            </div>
          )}

          <div className="mt-3 flex flex-wrap justify-center gap-3 text-sm text-gray-200">
            <Link to="/syntramfg" className="hover:text-yellow-300">Home</Link>
            <Link to="/syntramfg-updates" className="hover:text-yellow-300">Updates</Link>
          </div>
        </div>
      )}

      {/* Main content: Provided sections */}
      <main className="flex-grow">
        <section
          id="whyus"
          className="py-6 px-4 sm:px-6 md:px-20 bg-gradient-to-r from-cyan-600 to-cyan-700"
        >
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-4 text-white">
            Why Choose SyntraMfg?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 text-center text-white max-w-6xl mx-auto">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="bg-white/10 p-3 sm:p-4 rounded-xl shadow-lg backdrop-blur-lg hover:bg-white/20 transition"
              >
                <div className="text-2xl sm:text-3xl mb-3">{feature.emoji}</div>
                <h4 className="text-base sm:text-lg font-semibold mb-2">{feature.title}</h4>
                <p className="text-xs sm:text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SyntraMfg – Growth, Engagement & Empowerment */}
        <section id="syntramfg" className="py-6 sm:py-8 px-4 sm:px-6 md:px-20 bg-gradient-to-r from-cyan-700 to-cyan-800 text-white">
          <h2 className="text-lg sm:text-xl font-bold text-center mb-2">SyntraMfg – Your Daily Growth & Reflection Buddy</h2>
          <div className="max-w-6xl mx-auto space-y-4">

            {/* Core Features */}
            <div className="bg-white/10 p-2 sm:p-4 rounded-xl shadow-lg backdrop-blur-lg hover:bg-white/20 transition">
              <h3 className="text-sm sm:text-base font-semibold mb-3">✨ Core Features</h3>
              <ul className="list-disc list-inside text-xs sm:text-sm space-y-1">
                <li><strong>📝 Production Log</strong> — Daily logs for operations, quality checks, and process improvements.</li>
                <li><strong>👏 Team Applause</strong> — Celebrate team wins (big or small), appreciate colleagues and partners.</li>
                <li><strong>🔍 Root Cause Analysis (RCA)</strong> — Learn to ask “Why?” multiple times, turn issues into solutions, build a problem-solving mindset.</li>
                <li><strong>📖 Operations Diary / Yearbook</strong> — Auto-compiled from logs, applause & RCA → downloadable operations memory book.</li>
              </ul>
            </div>

            {/* Engagement & Recognition */}
            <div className="bg-white/10 p-2 sm:p-4 rounded-xl shadow-lg backdrop-blur-lg hover:bg-white/20 transition">
              <h3 className="text-sm sm:text-base font-semibold mb-1">✨ Celebrate Wins, Stay Inspired</h3>
              <ul className="list-disc list-inside text-xs sm:text-sm space-y-1">
                <li><strong>💳 Manufacturer ID Card</strong> — Create & share your unique digital identity.</li>
                <li><strong>⚡ Skill Fuel Quizzes</strong> — Daily 3-question challenges (manufacturing knowledge, industry trends, innovation, safety).</li>
                <li><strong>📰 Manufacturing News</strong> — Daily top 10 updates from the manufacturing world.</li>
                <li><strong>🏆 Leaderboard & Rewards</strong> — Track activities, earn points, win exciting prizes & badges.</li>
                <li><strong>🎙️ Workshops & Podcasts</strong> — Interactive talks on manufacturing, innovation & self-growth.</li>
                <li><strong>🎮 SyntraMfg Games</strong> — Quick, fun simulations & logic puzzles to sharpen manufacturing thinking.</li>
              </ul>
            </div>

            {/* Counselling, Commerce & Contribution */}
            <div className="bg-white/10 p-2 sm:p-4 rounded-xl shadow-lg backdrop-blur-lg hover:bg-white/20 transition">
              <h3 className="text-sm sm:text-base font-semibold mb-1">✨ Empowering Manufacturers & Innovators Beyond Operations</h3>
              <ul className="list-disc list-inside text-xs sm:text-sm space-y-1">
                <li><strong>🤖 AI Buddy Counselling</strong> — 24x7 judgment-free support for operations, teams, stress, or strategy doubts.</li>
                <li><strong>👩‍🏫 Expert Mentoring</strong> — Pay-per-minute access to real mentors, engineers & industry guides.</li>
                <li><strong>👛 SyntraMfg Wallet</strong> — Hassle-free payments & prize redemptions.</li>
                <li><strong>👭 Referral & Ambassador Program</strong> — Earn points/rewards by inviting partners (multi-level rewards).</li>
                <li><strong>🛒 Marketplace</strong> — Buy/sell parts, tools, services & digital creations.</li>
                <li><strong>🎁 SyntraMfg Collectibles</strong> — Earn, trade, gift or sell digital collectibles & merch.</li>
                <li><strong>⏱️ Focus Timer</strong> — Stay productive with Pomodoro-style focus sessions.</li>
                <li><strong>📝 Creator Hub (Upcoming)</strong> — Share innovations, videos, designs & monetize your expertise.</li>
                <li><strong>📊 Dashboard</strong> — Visualize operations, habits, growth & progress in one place.</li>
                <li><strong>🌍 Industry Builder</strong> — Every learning, reflection & activity adds to your growth → and India’s manufacturing future.</li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 text-gray-400 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-cyan-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-cyan-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-cyan-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-cyan-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SyntraMfgAbout;